import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  GamePassIncrementUserTaskRequest,
  GamePassIncrementUserTaskResponse,
  IncrementUserTaskVariables,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";

const buildIncrementUserTaskUrl = (userId: string, taskId: string): string =>
  API_ROUTES.INCREMENT_GAME_PASS_USER_TASK.replace("{user_id}", userId).replace(
    "{task_id}",
    taskId,
  );

const assertIncrementUserTaskSuccess = (
  data: GamePassIncrementUserTaskResponse | undefined,
): GamePassIncrementUserTaskResponse => {
  if (data && typeof data === "object" && data.success === false) {
    const message =
      typeof data.message === "string" && data.message.trim()
        ? data.message.trim()
        : "Failed to increment task progress";

    throw Object.assign(new Error(message), {
      response: { data },
    });
  }

  return data ?? {};
};

const postIncrementUserTask = async ({
  userId,
  taskId,
  reason,
  amount = 1,
  betId,
}: IncrementUserTaskVariables): Promise<GamePassIncrementUserTaskResponse> => {
  const body: GamePassIncrementUserTaskRequest = {
    amount,
    reason,
    ...(betId ? { bet_id: betId } : {}),
  };
  const response =
    await getPrivateInstance().post<GamePassIncrementUserTaskResponse>(
      buildIncrementUserTaskUrl(userId, taskId),
      body,
    );
  return assertIncrementUserTaskSuccess(response.data);
};

const refreshUserProgressQueries = async (
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
) => {
  await queryClient.refetchQueries({
    queryKey: ["game-pass", "users", "progress", userId],
  });
  await queryClient.refetchQueries({
    queryKey: ["game-pass", "users", "stats", userId],
  });
};

/**
 * Support remediation: `POST …/tasks/{task_id}/increment`.
 * Adds `amount` to count/streak without force-completing; auto-completes when
 * the bump crosses `target_value`. Works for season and campaign tasks — the
 * `task_id` carries the container relationship server-side.
 */
export const useIncrementGamePassUserTask = (): UseMutationResult<
  GamePassIncrementUserTaskResponse,
  Error,
  IncrementUserTaskVariables
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "users", "tasks", "increment"],
    mutationFn: postIncrementUserTask,
    onSuccess: async (_data, variables) => {
      await refreshUserProgressQueries(queryClient, variables.userId);
    },
  });
};

export {
  assertIncrementUserTaskSuccess,
  buildIncrementUserTaskUrl,
  postIncrementUserTask,
};
